import React, { useEffect, useRef } from "react";

export default function AddressAutocompleteInput({
  value,
  onChange,
  onPlaceSelected,
  placeholder,
  disabled = false,
  countryRestriction = ["br"],
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const skipSyncRef = useRef(false);

  // Refs "espelho" pra sempre expor a versão mais recente dos callbacks
  // pro listener do Google, que é registrado uma única vez.
  const onChangeRef = useRef(onChange);
  const onPlaceSelectedRef = useRef(onPlaceSelected);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onPlaceSelectedRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  useEffect(() => {
    if (!inputRef.current) return;
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    const next = value || "";
    if (inputRef.current.value !== next) {
      inputRef.current.value = next;
    }
  }, [value]);

  useEffect(() => {
    let retryTimeout;

    const initAutocomplete = () => {
      if (!window.google?.maps?.places) {
        retryTimeout = setTimeout(initAutocomplete, 150);
        return;
      }
      if (!inputRef.current) {
        retryTimeout = setTimeout(initAutocomplete, 150);
        return;
      }

      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }

      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          fields: ["geometry", "formatted_address"],
          componentRestrictions: countryRestriction
            ? { country: countryRestriction }
            : undefined,
        }
      );

      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (place?.geometry?.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const address = place.formatted_address || place.name || "";
          skipSyncRef.current = true;
          // Sempre chama a versão MAIS RECENTE dos callbacks (via ref),
          // e não a que foi capturada quando o listener foi criado.
          onChangeRef.current?.(address);
          onPlaceSelectedRef.current?.({ address, latitude: lat, longitude: lng });
        }
      });

      // Prevent pac-container clicks from being intercepted by modal backdrop
      // by stopping mousedown propagation on the dropdown container
      const pacObserver = new MutationObserver(() => {
        const pacs = document.querySelectorAll(".pac-container");
        pacs.forEach((pac) => {
          if (!pac._mousedownPatched) {
            pac._mousedownPatched = true;
            pac.addEventListener("mousedown", (e) => {
              e.stopPropagation();
            });
          }
        });
      });

      pacObserver.observe(document.body, { childList: true, subtree: true });

      // Patch already-existing pac-containers
      document.querySelectorAll(".pac-container").forEach((pac) => {
        if (!pac._mousedownPatched) {
          pac._mousedownPatched = true;
          pac.addEventListener("mousedown", (e) => {
            e.stopPropagation();
          });
        }
      });

      return pacObserver;
    };

    const observer = initAutocomplete();

    return () => {
      clearTimeout(retryTimeout);
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      if (observer?.disconnect) observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <input
      ref={inputRef}
      type="text"
      className="form-control"
      defaultValue={value || ""}
      onChange={(e) => {
        skipSyncRef.current = false;
        onChange?.(e.target.value);
      }}
      placeholder={placeholder}
      autoComplete="off"
      disabled={disabled}
    />
  );
}
